import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

type Tone = "success" | "error" | "warning";

interface ChangeBadgeProps {
  /** Signed percentage change vs the previous comparable period. */
  percent: number;
  /** e.g. "จากเมื่อวาน" / "จากเดือนก่อน" / "จากปีก่อน" — shown under the pill. */
  label?: string;
  /**
   * Force the pill colour. When omitted the colour derives from the sign
   * (≥0 → success/green, <0 → error/red). Leftover cards pass "warning"
   * (orange) and waste cards pass "error" (red) to match the design.
   */
  tone?: Tone;
}

const TONE_COLOR: Record<Tone, string> = {
  success: "#45be77",
  error: "#fb3b3b",
  warning: "#FDA42C",
};

/** Bare solid "+10%" pill (no label). */
function ChangePill({ percent, tone }: { percent: number; tone?: Tone }) {
  const resolvedTone: Tone = tone ?? (percent >= 0 ? "success" : "error");
  const color = TONE_COLOR[resolvedTone];
  const sign = percent > 0 ? "+" : "";

  return (
    <Box
      component="span"
      sx={{
        bgcolor: color,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.75rem",
        lineHeight: 1.4,
        borderRadius: 5,
        px: 1,
        py: 0.1,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {sign}
      {percent}%
    </Box>
  );
}

/**
 * Small solid "+10%" pill with an optional period-relative label stacked
 * underneath it, right-aligned. Matches the waste-leftover design
 * (top-right corner of each card).
 */
export default function ChangeBadge({ percent, label, tone }: ChangeBadgeProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 0.5,
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <ChangePill percent={percent} tone={tone} />
      {label && (
        <Typography
          color="text.secondary"
          variant="caption"
          noWrap
          sx={{ fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}
